import { Drawer, styled, Typography } from '@mui/material';
import { type ReactNode, useId } from 'react';

import { CloseIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';

type Props = {
  open: boolean;
  onClose: () => void;
  closeLabel: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  'data-testid'?: string;
};

const MAX_HEIGHT = '92dvh';
const HANDLE_WIDTH = 36;
const HANDLE_HEIGHT = 4;

const Sheet = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: MAX_HEIGHT,
  backgroundColor: theme.palette.background.default,
}));

const HandleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

const Handle = styled('span')(({ theme }) => ({
  width: HANDLE_WIDTH,
  height: HANDLE_HEIGHT,
  borderRadius: HANDLE_HEIGHT,
  backgroundColor: theme.palette.action.disabled,
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderText = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Subtitle = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Content = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(2),
  paddingBottom: `max(${theme.spacing(3)}, env(safe-area-inset-bottom))`,
}));

/** Panel sliding up from the bottom of a phone screen to consult content without leaving the current task. */
export function BottomSheet(props: Props) {
  const titleId = useId();

  return (
    <Drawer anchor="bottom" open={props.open} onClose={props.onClose} aria-labelledby={titleId}>
      <Sheet data-testid={props['data-testid'] ?? 'bottom-sheet'}>
        <HandleRow aria-hidden="true">
          <Handle />
        </HandleRow>
        <Header>
          <HeaderText>
            <Typography id={titleId} variant="subtitle1" component="h2">
              {props.title}
            </Typography>
            {props.subtitle ? (
              <Subtitle variant="body2" color="text.secondary">
                {props.subtitle}
              </Subtitle>
            ) : null}
          </HeaderText>
          <IconButton tooltip={props.closeLabel} edge="end" onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>{props.children}</Content>
      </Sheet>
    </Drawer>
  );
}

export type { Props as BottomSheetProps };
